/**
 * Formulaire unifié pour créer ou modifier une localisation (organisation, refuge, camping).
 * Champs complets pour les refuges (alignés sur l'admin Django).
 */

import { useEffect, useState, useCallback } from 'react'
import type {
  CreateLocationData,
  OrganizationLocationDetail,
  PricingProfile,
  PricingSource,
  UpdateLocationData,
} from '../types'
import {
  useCreateOrganizationLocation,
  useUpdateOrganizationLocation,
} from '../hooks/useOrganizationLocations'
import { useOrganization } from '../hooks/useOrganization'
import { HttpError, listPricingProfiles } from '../services/organizationApi'
import { MapPicker, type MapPickerPoint } from './MapPicker'
import { PrimaryGalleryEditor } from './PrimaryGalleryEditor'
import { useFilterOptions } from '../../list-search/hooks/useFilterOptions'
import { useCountries, useProvinces, useActivities } from '../../list-search/hooks/useGeographicFilters'
import {
  showsRefugeDormitoryPrice,
  showsRefugeExclusiveOccupation,
  showsRefugeExclusivePrice,
} from '../../../shared/utils/refugePricingUtils'
import { LocationFormPricingSection } from './LocationFormPricingSection'
import {
  PRICING_MODE_OPTIONS,
  type PricingModeOption,
} from './locationFormPricingConstants'
import './LocationManagementForm.css'
import { useTranslation } from 'react-i18next'
import i18n from '../../../i18n'

const LOCATION_TYPES = [
  { value: 'refuge', get label() { return i18n.t('organizations:LocationManagementForm.refuge') } },
  { value: 'camping', get label() { return i18n.t('organizations:LocationManagementForm.camping') } },
  { value: 'organization', get label() { return i18n.t('organizations:LocationManagementForm.organisation') } },
] as const

const STATUS_OPTIONS = [
  { value: 'active', get label() { return i18n.t('organizations:LocationManagementForm.publie') } },
  { value: 'draft', get label() { return i18n.t('organizations:LocationManagementForm.brouillon') } },
] as const

const DRINKING_WATER_OPTIONS = [
  { value: '', label: '—' },
  { value: 'potable', get label() { return i18n.t('organizations:LocationManagementForm.eau_potable') } },
  { value: 'filtered', get label() { return i18n.t('organizations:LocationManagementForm.eau_filtree') } },
  { value: 'non_potable', get label() { return i18n.t('organizations:LocationManagementForm.eau_non_potable') } },
  { value: 'none', get label() { return i18n.t('organizations:LocationManagementForm.sans_eau') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.pas_eau_potable') } },
] as const

const TOILET_OPTIONS = [
  { value: '', label: '—' },
  { value: 'yes', get label() { return i18n.t('organizations:LocationManagementForm.oui') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.non') } },
  { value: 'vault', get label() { return i18n.t('organizations:LocationManagementForm.toilette_seche') } },
  { value: 'flush', get label() { return i18n.t('organizations:LocationManagementForm.toilettes_avec_chasse') } },
] as const

const DOGS_ALLOWED_OPTIONS = [
  { value: '', label: '—' },
  { value: 'yes', get label() { return i18n.t('organizations:LocationManagementForm.oui') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.non') } },
] as const

const CAR_ACCESS_OPTIONS = [
  { value: '', label: '—' },
  { value: 'yes', get label() { return i18n.t('organizations:LocationManagementForm.oui') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.non') } },
] as const

const RENTAL_TYPE_OPTIONS = [
  { value: '', label: '—' },
  { value: 'Dortoir', get label() { return i18n.t('organizations:LocationManagementForm.dortoir') } },
  { value: 'Exclusive', get label() { return i18n.t('organizations:LocationManagementForm.exclusive') } },
  // i18n-ignore : value = valeur enregistrée en base, seul label est traduit
  { value: 'Dortoir ou exclusive', get label() { return i18n.t('organizations:LocationManagementForm.dortoir_ou_exclusive') } },
  { value: 'N/A', label: 'N/A' },
] as const

const EXCLUSIVE_OCCUPATION_OPTIONS = [
  { value: '', label: '—' },
  { value: 'yes', get label() { return i18n.t('organizations:LocationManagementForm.oui') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.non') } },
] as const

const COST_OPTIONS = [
  { value: '', label: '—' },
  { value: 'yes', get label() { return i18n.t('organizations:LocationManagementForm.oui') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.non') } },
] as const

const RESERVATION_OPTIONS = [
  { value: '', label: '—' },
  // i18n-ignore : value = valeur enregistrée en base, seul label est traduit
  { value: 'Non disponible', get label() { return i18n.t('organizations:LocationManagementForm.non_disponible') } },
  { value: 'Obligatoire', get label() { return i18n.t('organizations:LocationManagementForm.obligatoire') } },
  { value: 'Possible', get label() { return i18n.t('organizations:LocationManagementForm.possible') } },
] as const

const SHOWERS_OPTIONS = [
  { value: '', label: '—' },
  { value: 'yes', get label() { return i18n.t('organizations:LocationManagementForm.oui') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.non') } },
  { value: 'extra', get label() { return i18n.t('organizations:LocationManagementForm.avec_supplement') } },
] as const

const DOG_FRIENDLY_OPTIONS = [
  { value: '', label: '—' },
  { value: 'yes', get label() { return i18n.t('organizations:LocationManagementForm.oui') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.non') } },
  { value: 'restricted', get label() { return i18n.t('organizations:LocationManagementForm.avec_restrictions') } },
] as const

const WELCOME_CYCLIST_OPTIONS = [
  { value: '', label: '—' },
  { value: 'yes', get label() { return i18n.t('organizations:LocationManagementForm.oui') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.non') } },
  { value: 'unknown', get label() { return i18n.t('organizations:LocationManagementForm.inconnu') } },
] as const

const TENT_ONLY_OPTIONS = [
  { value: '', label: '—' },
  { value: 'yes', get label() { return i18n.t('organizations:LocationManagementForm.oui') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.non') } },
  { value: 'unknown', get label() { return i18n.t('organizations:LocationManagementForm.inconnu') } },
] as const

const FEE_OPTIONS = [
  { value: '', label: '—' },
  { value: 'yes', get label() { return i18n.t('organizations:LocationManagementForm.oui') } },
  { value: 'no', get label() { return i18n.t('organizations:LocationManagementForm.non') } },
] as const

/** Onglets alignés sur le panel gestion (camping / refuge uniquement). */
type LocationFormTab =
  | 'info'
  | 'equip'
  | 'activities'
  | 'pricing'
  | 'location'
  | 'photos'

const LOCATION_FORM_TABS: { id: LocationFormTab; label: string }[] = [
  { id: 'info', get label() { return i18n.t('organizations:LocationManagementForm.informations') } },
  { id: 'equip', get label() { return i18n.t('organizations:LocationManagementForm.equipements_acces') } },
  { id: 'activities', get label() { return i18n.t('organizations:LocationManagementForm.activites') } },
  { id: 'pricing', get label() { return i18n.t('organizations:LocationManagementForm.tarification_reservation') } },
  { id: 'location', get label() { return i18n.t('organizations:LocationManagementForm.localisation') } },
  { id: 'photos', get label() { return i18n.t('organizations:LocationManagementForm.photos') } },
]

interface LocationManagementFormProps {
  organizationId: number
  location?: OrganizationLocationDetail | null
  defaultType?: string
  onSuccess?: () => void
  onCancel?: () => void
}

export function LocationManagementForm({
  organizationId,
  location,
  defaultType = 'refuge',
  onSuccess,
  onCancel,
}: LocationManagementFormProps) {
  const { t: tr } = useTranslation('organizations')
  const isEditMode = Boolean(location?.id)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<string>(defaultType)
  const [point, setPoint] = useState<MapPickerPoint | null>(() =>
    location?.coordinates &&
    Number.isFinite(location.coordinates.lat) &&
    Number.isFinite(location.coordinates.lng)
      ? { lat: location.coordinates.lat, lng: location.coordinates.lng }
      : null,
  )
  const [status, setStatus] = useState<string>('active')
  const [error, setError] = useState<string | null>(null)

  const resolvedType = location?.type ?? type ?? defaultType
  const isRefuge = resolvedType === 'refuge'
  const isCamping = resolvedType === 'camping'

  // Refuge-specific state
  const [buildingTypeId, setBuildingTypeId] = useState<number | ''>('')
  const [osmId, setOsmId] = useState<string>('')
  const [capacity, setCapacity] = useState<string>('')
  const [drinkingWater, setDrinkingWater] = useState('')
  const [toilet, setToilet] = useState('')
  const [dogsAllowed, setDogsAllowed] = useState('')
  const [carAccessSummer, setCarAccessSummer] = useState('')
  const [carAccessWinter, setCarAccessWinter] = useState('')
  const [rentalType, setRentalType] = useState('')
  const [exclusiveOccupation, setExclusiveOccupation] = useState('')
  const [cost, setCost] = useState('')
  const [dormitoryPrice, setDormitoryPrice] = useState('')
  const [exclusivePrice, setExclusivePrice] = useState('')
  const [priceDate, setPriceDate] = useState<string>('')
  const [reservation, setReservation] = useState('')
  const [reservationUrl, setReservationUrl] = useState('')
  const [reservationEmail, setReservationEmail] = useState('')
  const [reservationPhone, setReservationPhone] = useState('')
  const [countryId, setCountryId] = useState<number | ''>('')
  const [provinceId, setProvinceId] = useState<number | ''>('')
  const [summerPrice, setSummerPrice] = useState('')
  const [winterPrice, setWinterPrice] = useState('')
  const [summerAvailable, setSummerAvailable] = useState(true)
  const [winterAvailable, setWinterAvailable] = useState(true)
  const [activityIds, setActivityIds] = useState<number[]>([])

  // Camping-specific state
  const [campingTypeId, setCampingTypeId] = useState<number | ''>('')
  const [operatorText, setOperatorText] = useState('')
  const [elevation, setElevation] = useState('')
  const [emplacementTent, setEmplacementTent] = useState('')
  const [emplacementRv, setEmplacementRv] = useState('')
  const [showers, setShowers] = useState('')
  const [dogFriendly, setDogFriendly] = useState('')
  const [welcomeCyclist, setWelcomeCyclist] = useState('')
  const [tentOnly, setTentOnly] = useState('')
  const [fee, setFee] = useState('')
  const [placementTypeIds, setPlacementTypeIds] = useState<number[]>([])
  const [activeTab, setActiveTab] = useState<LocationFormTab>('info')
  const [pricingMode, setPricingMode] = useState<PricingModeOption>('simple')
  const [pricingSource, setPricingSource] = useState<PricingSource>('local')
  const [pricingProfileId, setPricingProfileId] = useState<number | ''>('')
  const [availableProfiles, setAvailableProfiles] = useState<PricingProfile[]>([])
  const [simplePriceMin, setSimplePriceMin] = useState('')
  const [simplePriceMax, setSimplePriceMax] = useState('')
  const [taxesIncluded, setTaxesIncluded] = useState(true)
  const [accessIncluded, setAccessIncluded] = useState(true)
  const [dormitoryPriceMax, setDormitoryPriceMax] = useState('')
  const [exclusivePriceMax, setExclusivePriceMax] = useState('')
  const [priceMin, setPriceMin] = useState('')

  const showTabs = isRefuge || isCamping
  const showRefugeDormitoryPriceField =
    pricingMode === 'simple'
      ? showsRefugeDormitoryPrice(rentalType) || !rentalType
      : cost === 'yes' && showsRefugeDormitoryPrice(rentalType)
  const showRefugeExclusivePriceField =
    pricingMode === 'simple'
      ? showsRefugeExclusivePrice(rentalType) || !rentalType
      : cost === 'yes' && showsRefugeExclusivePrice(rentalType)
  const showRefugeExclusiveOccupationField = showsRefugeExclusiveOccupation(rentalType)
  const showRefugePriceDateField = cost === 'yes' && (showRefugeDormitoryPriceField || showRefugeExclusivePriceField)

  const { data: filterOptions } = useFilterOptions()
  const { data: countries = [] } = useCountries()
  const { data: provinces = [] } = useProvinces(countryId || undefined)
  const { data: activities = [] } = useActivities()
  const operatorId = location?.operator ?? organizationId
  const { data: organization } = useOrganization(
    operatorId != null && operatorId > 0 ? operatorId : null,
    { enabled: operatorId != null && operatorId > 0 },
  )

  const createMutation = useCreateOrganizationLocation(organizationId)
  const updateMutation = useUpdateOrganizationLocation(
    organizationId,
    location?.id ?? 0,
    { locationType: location?.type ?? defaultType },
  )

  useEffect(() => {
    if (location) {
      setName(location.name ?? '')
      setType(location.type ?? 'refuge')
      setStatus(location.status ?? 'active')
      if (location.coordinates) {
        setPoint({ lat: location.coordinates.lat, lng: location.coordinates.lng })
      } else {
        setPoint(null)
      }
      setDescription(location.description ?? '')

      if (location.type === 'refuge') {
        const r = location
        setBuildingTypeId(r.building_type_id ?? '')
        setOsmId(r.osm_id != null ? String(r.osm_id) : '')
        setCapacity(r.capacity != null ? String(r.capacity) : '')
        setDrinkingWater(r.drinking_water ?? '')
        setToilet(r.toilet ?? '')
        setDogsAllowed(r.dogs_allowed ?? '')
        setCarAccessSummer(r.car_access_summer ?? '')
        setCarAccessWinter(r.car_access_winter ?? '')
        setRentalType(r.rental_type ?? '')
        setExclusiveOccupation(r.exclusive_occupation ?? '')
        setCost(r.cost ?? '')
        setDormitoryPrice(r.dormitory_price ?? '')
        setExclusivePrice(r.exclusive_price ?? '')
        setPriceDate(r.price_date != null ? String(r.price_date) : '')
        setReservation(r.reservation ?? '')
        setReservationUrl(r.reservation_url ?? '')
        setReservationEmail(r.reservation_email ?? '')
        setReservationPhone(r.reservation_phone ?? '')
        setCountryId(r.country_id ?? '')
        setProvinceId(r.province_id ?? '')
        setSummerPrice(r.summer_price ?? '')
        setWinterPrice(r.winter_price ?? '')
        setSummerAvailable(r.summer_available ?? true)
        setWinterAvailable(r.winter_available ?? true)
        setActivityIds(r.activity_ids ?? [])
        setPricingMode((r.pricing_mode as PricingModeOption) || 'simple')
        setPricingSource((r.pricing_source as PricingSource) === 'profile' ? 'profile' : 'local')
        setPricingProfileId(r.pricing_profile_id ?? '')
        setAvailableProfiles(r.available_profiles ?? [])
        setSimplePriceMin(r.simple_price_min != null && r.simple_price_min !== '' ? String(r.simple_price_min) : '')
        setSimplePriceMax(r.simple_price_max != null && r.simple_price_max !== '' ? String(r.simple_price_max) : '')
        setTaxesIncluded(r.taxes_included ?? true)
        setAccessIncluded(r.access_included ?? true)
        setDormitoryPriceMax(r.dormitory_price_max != null && r.dormitory_price_max !== '' ? String(r.dormitory_price_max) : '')
        setExclusivePriceMax(r.exclusive_price_max != null && r.exclusive_price_max !== '' ? String(r.exclusive_price_max) : '')
      } else if (location.type === 'camping') {
        const c = location
        setCampingTypeId(c.camping_type_id ?? '')
        setOperatorText(c.operator_text ?? '')
        setOsmId(c.osm_id != null ? String(c.osm_id) : '')
        setElevation(c.elevation ?? '')
        setCapacity(c.capacity != null ? String(c.capacity) : '')
        setEmplacementTent(c.emplacement_tent != null ? String(c.emplacement_tent) : '')
        setEmplacementRv(c.emplacement_rv != null ? String(c.emplacement_rv) : '')
        setDrinkingWater(c.drinking_water ?? '')
        setToilet(c.toilet ?? '')
        setShowers(c.showers ?? '')
        setDogFriendly(c.dog_friendly ?? '')
        setWelcomeCyclist(c.welcome_cyclist ?? '')
        setTentOnly(c.tent_only ?? '')
        setCarAccessSummer(c.car_access_summer ?? '')
        setCarAccessWinter(c.car_access_winter ?? '')
        setFee(c.fee ?? '')
        setPriceDate(c.price_date != null ? String(c.price_date) : '')
        setSummerPrice(c.summer_price ?? '')
        setWinterPrice(c.winter_price ?? '')
        setSummerAvailable(c.summer_available ?? true)
        setWinterAvailable(c.winter_available ?? true)
        setReservation(c.reservation ?? '')
        setReservationUrl(c.reservation_url ?? '')
        setReservationEmail(c.reservation_email ?? '')
        setReservationPhone(c.reservation_phone ?? '')
        setCountryId(c.country_id ?? '')
        setProvinceId(c.province_id ?? '')
        setActivityIds(c.activity_ids ?? [])
        setPlacementTypeIds(c.placement_type_ids ?? [])
        setPricingMode((c.pricing_mode as PricingModeOption) || 'simple')
        setPricingSource((c.pricing_source as PricingSource) === 'profile' ? 'profile' : 'local')
        setPricingProfileId(c.pricing_profile_id ?? '')
        setAvailableProfiles(c.available_profiles ?? [])
        setSimplePriceMin(c.simple_price_min != null && c.simple_price_min !== '' ? String(c.simple_price_min) : '')
        setSimplePriceMax(c.simple_price_max != null && c.simple_price_max !== '' ? String(c.simple_price_max) : '')
        setTaxesIncluded(c.taxes_included ?? true)
        setAccessIncluded(c.access_included ?? true)
        setPriceMin(c.price_min != null && c.price_min !== '' ? String(c.price_min) : '')
      }
    } else {
      setName('')
      setDescription('')
      setType(defaultType)
      setPoint(null)
      setStatus('active')
      setBuildingTypeId('')
      setOsmId('')
      setCapacity('')
      setDrinkingWater('')
      setToilet('')
      setDogsAllowed('')
      setCarAccessSummer('')
      setCarAccessWinter('')
      setRentalType('')
      setExclusiveOccupation('')
      setCost('')
      setDormitoryPrice('')
      setExclusivePrice('')
      setPriceDate('')
      setReservation('')
      setReservationUrl('')
      setReservationEmail('')
      setReservationPhone('')
      setCountryId('')
      setProvinceId('')
      setSummerPrice('')
      setWinterPrice('')
      setSummerAvailable(true)
      setWinterAvailable(true)
      setActivityIds([])
      setCampingTypeId('')
      setOperatorText('')
      setElevation('')
      setEmplacementTent('')
      setEmplacementRv('')
      setShowers('')
      setDogFriendly('')
      setWelcomeCyclist('')
      setTentOnly('')
      setFee('')
      setPlacementTypeIds([])
      setPricingMode('simple')
      setPricingSource('local')
      setPricingProfileId('')
      setAvailableProfiles([])
      setSimplePriceMin('')
      setSimplePriceMax('')
      setTaxesIncluded(true)
      setAccessIncluded(true)
      setDormitoryPriceMax('')
      setExclusivePriceMax('')
      setPriceMin('')
    }
  }, [location, defaultType])

  useEffect(() => {
    if (!(isCamping || isRefuge) || !organizationId) return
    if (location) return
    const appliesTo = isCamping ? 'camping' : 'refuge'
    const ac = new AbortController()
    listPricingProfiles(organizationId, {
      applies_to: appliesTo,
      include_ancestors: true,
      signal: ac.signal,
    })
      .then((profiles) => setAvailableProfiles(profiles))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
      })
    return () => ac.abort()
  }, [organizationId, isCamping, isRefuge, location])

  const validate = useCallback((): boolean => {
    setError(null)
    if (!name.trim()) {
      setError(tr('LocationManagementForm.nom_requis'))
      return false
    }
    if (!isEditMode && !type) {
      setError(tr('LocationManagementForm.type_lieu_requis'))
      return false
    }
    if (!point || point.lat < -90 || point.lat > 90 || point.lng < -180 || point.lng > 180) {
      setError(tr('LocationManagementForm.veuillez_indiquer_lieu_carte_ou'))
      return false
    }
    if ((isCamping || isRefuge) && pricingSource === 'profile' && !pricingProfileId) {
      setError(tr('LocationManagementForm.choisissez_profil_tarifaire_ou_repassez'))
      return false
    }
    return true
  }, [name, isEditMode, type, point, isCamping, isRefuge, pricingSource, pricingProfileId])

  const buildPricingSourcePayload = (): Partial<UpdateLocationData> => ({
    pricing_source: pricingSource,
    pricing_profile_id: pricingSource === 'profile' && pricingProfileId !== ''
      ? pricingProfileId
      : null,
  })

  const buildRefugePayload = (): Partial<UpdateLocationData> => {
    if (!isRefuge) return {}
    const base: Partial<UpdateLocationData> = {
      building_type_id: buildingTypeId === '' ? null : buildingTypeId,
      osm_id: osmId.trim() ? parseInt(osmId, 10) || null : null,
      capacity: capacity.trim() ? parseInt(capacity, 10) || null : null,
      drinking_water: drinkingWater || undefined,
      toilet: toilet || undefined,
      dogs_allowed: dogsAllowed || undefined,
      car_access_summer: carAccessSummer || undefined,
      car_access_winter: carAccessWinter || undefined,
      rental_type: rentalType || undefined,
      exclusive_occupation: exclusiveOccupation || undefined,
      reservation: reservation || undefined,
      reservation_url: reservationUrl.trim() || undefined,
      reservation_email: reservationEmail.trim() || undefined,
      reservation_phone: reservationPhone.trim() || undefined,
      country_id: countryId === '' ? null : countryId,
      province_id: provinceId === '' ? null : provinceId,
      summer_available: summerAvailable,
      winter_available: winterAvailable,
      activity_ids: activityIds,
      ...buildPricingSourcePayload(),
    }
    if (pricingSource === 'profile') return base
    return {
      ...base,
      cost: cost || undefined,
      dormitory_price: dormitoryPrice.trim() ? parseFloat(dormitoryPrice) : null,
      exclusive_price: exclusivePrice.trim() ? parseFloat(exclusivePrice) : null,
      dormitory_price_max: dormitoryPriceMax.trim() ? parseFloat(dormitoryPriceMax) : null,
      exclusive_price_max: exclusivePriceMax.trim() ? parseFloat(exclusivePriceMax) : null,
      price_date: priceDate.trim() ? parseInt(priceDate, 10) : null,
      pricing_mode: pricingMode,
      simple_price_min: simplePriceMin.trim() ? parseFloat(simplePriceMin) : null,
      simple_price_max: simplePriceMax.trim() ? parseFloat(simplePriceMax) : null,
      taxes_included: taxesIncluded,
      access_included: accessIncluded,
    }
  }

  const buildCampingPayload = (): Partial<UpdateLocationData> => {
    if (!isCamping) return {}
    const base: Partial<UpdateLocationData> = {
      camping_type_id: campingTypeId === '' ? null : campingTypeId,
      operator_text: operatorText.trim() || undefined,
      osm_id: osmId.trim() ? parseInt(osmId, 10) || null : null,
      elevation: elevation.trim() ? parseFloat(elevation) : null,
      capacity: capacity.trim() ? parseInt(capacity, 10) || null : null,
      emplacement_tent: emplacementTent.trim() ? parseInt(emplacementTent, 10) || null : null,
      emplacement_rv: emplacementRv.trim() ? parseInt(emplacementRv, 10) || null : null,
      drinking_water: drinkingWater || undefined,
      toilet: toilet || undefined,
      showers: showers || undefined,
      dog_friendly: dogFriendly || undefined,
      welcome_cyclist: welcomeCyclist || undefined,
      tent_only: tentOnly || undefined,
      car_access_summer: carAccessSummer || undefined,
      car_access_winter: carAccessWinter || undefined,
      summer_available: summerAvailable,
      winter_available: winterAvailable,
      reservation: reservation || undefined,
      reservation_url: reservationUrl.trim() || undefined,
      reservation_email: reservationEmail.trim() || undefined,
      reservation_phone: reservationPhone.trim() || undefined,
      country_id: countryId === '' ? null : countryId,
      province_id: provinceId === '' ? null : provinceId,
      activity_ids: activityIds,
      placement_type_ids: placementTypeIds,
      ...buildPricingSourcePayload(),
    }
    if (pricingSource === 'profile') return base
    return {
      ...base,
      fee: fee || undefined,
      price_min: priceMin.trim() ? parseFloat(priceMin) : null,
      price_date: priceDate.trim() ? parseInt(priceDate, 10) : null,
      pricing_mode: pricingMode,
      simple_price_min: simplePriceMin.trim() ? parseFloat(simplePriceMin) : null,
      simple_price_max: simplePriceMax.trim() ? parseFloat(simplePriceMax) : null,
      taxes_included: taxesIncluded,
      access_included: accessIncluded,
      summer_price: summerPrice.trim() ? parseFloat(summerPrice) : null,
      winter_price: winterPrice.trim() ? parseFloat(winterPrice) : null,
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate() || !point) return

    try {
      if (isEditMode) {
        const payload: UpdateLocationData = {
          name: name.trim(),
          coordinates: { lat: point.lat, lng: point.lng },
          status,
          description: description.trim() || undefined,
          ...buildRefugePayload(),
          ...buildCampingPayload(),
        }
        await updateMutation.mutateAsync(payload)
      } else {
        const payload: CreateLocationData = {
          name: name.trim(),
          type,
          coordinates: { lat: point.lat, lng: point.lng },
          status,
          description: description.trim() || undefined,
          ...(isRefuge ? buildRefugePayload() : {}),
          ...(isCamping ? buildCampingPayload() : {}),
        }
        await createMutation.mutateAsync(payload)
      }
      onSuccess?.()
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.message ? tr('LocationManagementForm.erreur', { message: err.message }) : tr('LocationManagementForm.erreur_lors_enregistrement'))
      } else if (err instanceof Error) {
        setError(err.message ? tr('LocationManagementForm.erreur', { message: err.message }) : tr('LocationManagementForm.erreur_lors_enregistrement'))
      } else {
        setError(tr('LocationManagementForm.erreur_lors_enregistrement'))
      }
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending
  const buildingTypes = filterOptions?.refuges ?? []
  const selectedCampingType = (filterOptions?.campings ?? []).find((ct) => ct.id === campingTypeId)
  const campingCapacityLabel = (() => {
    if (!selectedCampingType) return tr('LocationManagementForm.capacite')
    const slug = selectedCampingType.slug?.toLowerCase()
    const name = selectedCampingType.name?.toLowerCase()
    if (slug === 'camping' || (!slug && name === 'camping')) {
      return tr('LocationManagementForm.capacite_emplacements')
    }
    return tr('LocationManagementForm.capacite_emplacements_tente')
  })()

  return (
    <form
      className={`location-form${showTabs ? ' location-form--tabs' : ''}`}
      data-active-tab={showTabs ? activeTab : undefined}
      onSubmit={handleSubmit}
      noValidate
    >
      <h2 className="location-form__title">
        {isEditMode ? tr('LocationManagementForm.modifier_localisation') : tr('LocationManagementForm.ajouter_localisation')}
      </h2>

      {error && (
        <div className="location-form__error" role="alert">
          {error}
        </div>
      )}

      {showTabs && (
        <nav className="location-form__tabs" aria-label={tr('LocationManagementForm.sections_formulaire')}>
          <ul className="location-form__tabs-list" role="tablist">
            {LOCATION_FORM_TABS.map((tab) => (
              <li key={tab.id} className="location-form__tabs-item" role="presentation">
                <button
                  type="button"
                  role="tab"
                  id={`location-form-tab-${tab.id}`}
                  aria-selected={activeTab === tab.id}
                  aria-controls={`location-form-panel-${tab.id}`}
                  className={`location-form__tab${activeTab === tab.id ? ' location-form__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <section
        className="location-form__section location-form__pane"
        data-tab="info"
        id={showTabs ? 'location-form-panel-info' : undefined}
        role={showTabs ? 'tabpanel' : undefined}
        aria-labelledby={showTabs ? tr('LocationManagementForm.location_form_tab_info_location') : 'location-form-basics'}
        hidden={showTabs && activeTab !== 'info'}
      >
        <h3 id="location-form-basics" className="location-form__section-title">
          {tr('LocationManagementForm.informations_base')}
        </h3>
        {(isRefuge || isCamping) && (organization != null || operatorId != null) && (
          <div className="location-form__field">
            <label className="location-form__label">{tr('LocationManagementForm.organisation')}</label>
            <div
              id="location-form-operator"
              className="location-form__readonly"
              aria-readonly
            >
              {organization?.name ?? tr('LocationManagementForm.organisation_2', { operatorId })}
            </div>
          </div>
        )}
        <div className="location-form__field">
          <label htmlFor="location-form-name" className="location-form__label">
            {tr('LocationManagementForm.nom')} <span className="location-form__required">*</span>
          </label>
          <input
            id="location-form-name"
            type="text"
            className="location-form__input"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(null) }}
            required
            disabled={isLoading}
            autoComplete="off"
          />
        </div>
        {!isEditMode && (
          <div className="location-form__field">
            <label htmlFor="location-form-type" className="location-form__label">
              {tr('LocationManagementForm.type')} <span className="location-form__required">*</span>
            </label>
            <select
              id="location-form-type"
              className="location-form__select"
              value={type}
              onChange={(e) => { setType(e.target.value); setError(null) }}
              required
              disabled={isLoading}
            >
              {LOCATION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        )}
        {isRefuge && (
          <div className="location-form__field">
            <label htmlFor="location-form-building-type" className="location-form__label">
              {tr('LocationManagementForm.type_batiment')}
            </label>
            <select
              id="location-form-building-type"
              className="location-form__select"
              value={buildingTypeId === '' ? '' : String(buildingTypeId)}
              onChange={(e) => setBuildingTypeId(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              disabled={isLoading}
            >
              <option value="">—</option>
              {buildingTypes.map((bt) => (
                <option key={bt.id} value={bt.id}>{bt.name}</option>
              ))}
            </select>
          </div>
        )}
        {isCamping && (
          <div className="location-form__field">
            <label htmlFor="location-form-camping-type" className="location-form__label">
              {tr('LocationManagementForm.type_camping')}
            </label>
            <select
              id="location-form-camping-type"
              className="location-form__select"
              value={campingTypeId === '' ? '' : String(campingTypeId)}
              onChange={(e) => setCampingTypeId(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              disabled={isLoading}
            >
              <option value="">—</option>
              {(filterOptions?.campings ?? []).map((ct) => (
                <option key={ct.id} value={ct.id}>{ct.name}</option>
              ))}
            </select>
          </div>
        )}
        {isCamping && (
          <div className="location-form__field">
            <label htmlFor="location-form-operator-text" className="location-form__label">
              {tr('LocationManagementForm.operateur_texte_alternatif')}
            </label>
            <input
              id="location-form-operator-text"
              type="text"
              className="location-form__input"
              value={operatorText}
              onChange={(e) => setOperatorText(e.target.value)}
              placeholder={tr('LocationManagementForm.si_pas_organisation_associee')}
              disabled={isLoading}
            />
          </div>
        )}
        {(isRefuge || isCamping) && (
          <div className="location-form__field">
            <label htmlFor="location-form-osm-id" className="location-form__label">
              {tr('LocationManagementForm.osm_id')}
            </label>
            <input
              id="location-form-osm-id"
              type="text"
              inputMode="numeric"
              className="location-form__input"
              value={osmId}
              onChange={(e) => setOsmId(e.target.value)}
              placeholder={tr('LocationManagementForm.id_openstreetmap')}
              disabled={isLoading}
            />
          </div>
        )}
        <div className="location-form__field">
          <label htmlFor="location-form-desc" className="location-form__label">
            {tr('LocationManagementForm.description_optionnel')}
          </label>
          <textarea
            id="location-form-desc"
            className="location-form__textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isLoading}
            rows={3}
            placeholder={tr('LocationManagementForm.quelques_phrases_decrire_lieu')}
          />
        </div>
      </section>

      <section
        className="location-form__section location-form__section--map location-form__pane"
        data-tab="location"
        id={showTabs ? 'location-form-panel-location' : undefined}
        role={showTabs ? 'tabpanel' : undefined}
        aria-labelledby={showTabs ? tr('LocationManagementForm.location_form_tab_location_location') : 'location-form-place'}
        hidden={showTabs && activeTab !== 'location'}
      >
        <h3 id="location-form-place" className="location-form__section-title">
          {tr('LocationManagementForm.lieu_carte')}
        </h3>
        <div className="location-form__map-wrap">
          <MapPicker
            value={point}
            onChange={setPoint}
            defaultCenter={
              point
                ? [point.lat, point.lng]
                : [46.3432, -72.5793]
            }
            defaultZoom={point ? 14 : 5}
            height="400px"
            disabled={isLoading}
            labelId="location-form-map-label"
          />
        </div>
      </section>

      <div className={showTabs ? 'location-form__panes' : 'location-form__grid'}>
      {(isRefuge || isCamping) && (
        <section
          className="location-form__section location-form__pane"
          data-tab="location"
          aria-labelledby="location-form-geo"
          hidden={showTabs && activeTab !== 'location'}
        >
          <h3 id="location-form-geo" className="location-form__section-title">
            {tr('LocationManagementForm.hierarchie_geographique')}
          </h3>
          <div className="location-form__field">
            <label htmlFor="location-form-country" className="location-form__label">
              {tr('LocationManagementForm.pays')}
            </label>
            <select
              id="location-form-country"
              className="location-form__select"
              value={countryId === '' ? '' : String(countryId)}
              onChange={(e) => {
                const v = e.target.value
                setCountryId(v === '' ? '' : parseInt(v, 10))
                setProvinceId('')
              }}
              disabled={isLoading}
            >
              <option value="">—</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-province" className="location-form__label">
              {tr('LocationManagementForm.province_region')}
            </label>
            <select
              id="location-form-province"
              className="location-form__select"
              value={provinceId === '' ? '' : String(provinceId)}
              onChange={(e) => setProvinceId(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              disabled={isLoading}
            >
              <option value="">—</option>
              {provinces.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </section>
      )}

      {isRefuge && (
        <section
          className="location-form__section location-form__pane"
          data-tab="equip"
          id={showTabs ? 'location-form-panel-equip' : undefined}
          role={showTabs ? 'tabpanel' : undefined}
          aria-labelledby={showTabs ? tr('LocationManagementForm.location_form_tab_equip_location') : 'location-form-equip'}
          hidden={showTabs && activeTab !== 'equip'}
        >
          <h3 id="location-form-equip" className="location-form__section-title">
            {tr('LocationManagementForm.equipements_capacite')}
          </h3>
          <div className="location-form__field">
            <label htmlFor="location-form-capacity" className="location-form__label">
              {tr('LocationManagementForm.capacite_personnes')}
            </label>
            <input
              id="location-form-capacity"
              type="number"
              min={0}
              className="location-form__input"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-drinking-water" className="location-form__label">
              {tr('LocationManagementForm.eau')}
            </label>
            <select
              id="location-form-drinking-water"
              className="location-form__select"
              value={drinkingWater}
              onChange={(e) => setDrinkingWater(e.target.value)}
              disabled={isLoading}
            >
              {DRINKING_WATER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-toilet" className="location-form__label">
              {tr('LocationManagementForm.toilettes')}
            </label>
            <select
              id="location-form-toilet"
              className="location-form__select"
              value={toilet}
              onChange={(e) => setToilet(e.target.value)}
              disabled={isLoading}
            >
              {TOILET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-dogs" className="location-form__label">
              {tr('LocationManagementForm.chiens_autorises')}
            </label>
            <select
              id="location-form-dogs"
              className="location-form__select"
              value={dogsAllowed}
              onChange={(e) => setDogsAllowed(e.target.value)}
              disabled={isLoading}
            >
              {DOGS_ALLOWED_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </section>
      )}

      {isCamping && (
        <section
          className="location-form__section location-form__pane"
          data-tab="equip"
          id={showTabs ? 'location-form-panel-equip' : undefined}
          role={showTabs ? 'tabpanel' : undefined}
          aria-labelledby={showTabs ? tr('LocationManagementForm.location_form_tab_equip_location_2') : 'location-form-equip-camping'}
          hidden={showTabs && activeTab !== 'equip'}
        >
          <h3 id="location-form-equip-camping" className="location-form__section-title">
            {tr('LocationManagementForm.equipements_capacite')}
          </h3>
          <div className="location-form__field">
            <label htmlFor="location-form-elevation" className="location-form__label">
              {tr('LocationManagementForm.elevation_m')}
            </label>
            <input
              id="location-form-elevation"
              type="number"
              step="0.01"
              className="location-form__input"
              value={elevation}
              onChange={(e) => setElevation(e.target.value)}
              placeholder={tr('LocationManagementForm.metres')}
              disabled={isLoading}
            />
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-capacity-camp" className="location-form__label">
              {campingCapacityLabel}
            </label>
            <input
              id="location-form-capacity-camp"
              type="number"
              min={0}
              className="location-form__input"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-emplacement-tent" className="location-form__label">
              {tr('LocationManagementForm.emplacements_tentes')}
            </label>
            <input
              id="location-form-emplacement-tent"
              type="number"
              min={0}
              className="location-form__input"
              value={emplacementTent}
              onChange={(e) => setEmplacementTent(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-emplacement-rv" className="location-form__label">
              {tr('LocationManagementForm.emplacements_vr')}
            </label>
            <input
              id="location-form-emplacement-rv"
              type="number"
              min={0}
              className="location-form__input"
              value={emplacementRv}
              onChange={(e) => setEmplacementRv(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-drinking-water-camp" className="location-form__label">
              {tr('LocationManagementForm.eau')}
            </label>
            <select
              id="location-form-drinking-water-camp"
              className="location-form__select"
              value={drinkingWater}
              onChange={(e) => setDrinkingWater(e.target.value)}
              disabled={isLoading}
            >
              {DRINKING_WATER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-toilet-camp" className="location-form__label">
              {tr('LocationManagementForm.toilettes')}
            </label>
            <select
              id="location-form-toilet-camp"
              className="location-form__select"
              value={toilet}
              onChange={(e) => setToilet(e.target.value)}
              disabled={isLoading}
            >
              {TOILET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-showers" className="location-form__label">
              {tr('LocationManagementForm.douches')}
            </label>
            <select
              id="location-form-showers"
              className="location-form__select"
              value={showers}
              onChange={(e) => setShowers(e.target.value)}
              disabled={isLoading}
            >
              {SHOWERS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-dog-friendly" className="location-form__label">
              {tr('LocationManagementForm.chiens_acceptes')}
            </label>
            <select
              id="location-form-dog-friendly"
              className="location-form__select"
              value={dogFriendly}
              onChange={(e) => setDogFriendly(e.target.value)}
              disabled={isLoading}
            >
              {DOG_FRIENDLY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-welcome-cyclist" className="location-form__label">
              {tr('LocationManagementForm.bienvenu_cycliste')}
            </label>
            <select
              id="location-form-welcome-cyclist"
              className="location-form__select"
              value={welcomeCyclist}
              onChange={(e) => setWelcomeCyclist(e.target.value)}
              disabled={isLoading}
            >
              {WELCOME_CYCLIST_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-tent-only" className="location-form__label">
              {tr('LocationManagementForm.tentes_seulement')}
            </label>
            <select
              id="location-form-tent-only"
              className="location-form__select"
              value={tentOnly}
              onChange={(e) => setTentOnly(e.target.value)}
              disabled={isLoading}
            >
              {TENT_ONLY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </section>
      )}

      {isCamping && (
        <section
          className="location-form__section location-form__pane"
          data-tab="equip"
          aria-labelledby="location-form-acces-camping"
          hidden={showTabs && activeTab !== 'equip'}
        >
          <h3 id="location-form-acces-camping" className="location-form__section-title">
            {tr('LocationManagementForm.acces')}
          </h3>
          <div className="location-form__field">
            <label htmlFor="location-form-car-access-summer-camp" className="location-form__label">
              {tr('LocationManagementForm.acces_voiture_ete')}
            </label>
            <select
              id="location-form-car-access-summer-camp"
              className="location-form__select"
              value={carAccessSummer}
              onChange={(e) => setCarAccessSummer(e.target.value)}
              disabled={isLoading}
            >
              {CAR_ACCESS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-car-access-winter-camp" className="location-form__label">
              {tr('LocationManagementForm.acces_voiture_hiver')}
            </label>
            <select
              id="location-form-car-access-winter-camp"
              className="location-form__select"
              value={carAccessWinter}
              onChange={(e) => setCarAccessWinter(e.target.value)}
              disabled={isLoading}
            >
              {CAR_ACCESS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </section>
      )}

      {isRefuge && (
        <section
          className="location-form__section location-form__pane"
          data-tab="equip"
          aria-labelledby="location-form-acces"
          hidden={showTabs && activeTab !== 'equip'}
        >
          <h3 id="location-form-acces" className="location-form__section-title">
            {tr('LocationManagementForm.acces')}
          </h3>
          <div className="location-form__field">
            <label htmlFor="location-form-car-access-summer" className="location-form__label">
              {tr('LocationManagementForm.acces_voiture_ete')}
            </label>
            <select
              id="location-form-car-access-summer"
              className="location-form__select"
              value={carAccessSummer}
              onChange={(e) => setCarAccessSummer(e.target.value)}
              disabled={isLoading}
            >
              {CAR_ACCESS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="location-form__field">
            <label htmlFor="location-form-car-access-winter" className="location-form__label">
              {tr('LocationManagementForm.acces_voiture_hiver')}
            </label>
            <select
              id="location-form-car-access-winter"
              className="location-form__select"
              value={carAccessWinter}
              onChange={(e) => setCarAccessWinter(e.target.value)}
              disabled={isLoading}
            >
              {CAR_ACCESS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </section>
      )}

      {(isCamping || isRefuge) && (
        <LocationFormPricingSection
          showTabs={showTabs}
          activeTabIsPricing={activeTab === 'pricing'}
          isCamping={isCamping}
          isRefuge={isRefuge}
          isLoading={isLoading}
          pricingSource={pricingSource}
          setPricingSource={setPricingSource}
          pricingProfileId={pricingProfileId}
          setPricingProfileId={setPricingProfileId}
          availableProfiles={availableProfiles}
          organizationId={organizationId}
          pricingMode={pricingMode}
          setPricingMode={setPricingMode}
          pricingModeOptions={PRICING_MODE_OPTIONS}
          taxesIncluded={taxesIncluded}
          setTaxesIncluded={setTaxesIncluded}
          accessIncluded={accessIncluded}
          setAccessIncluded={setAccessIncluded}
          simplePriceMin={simplePriceMin}
          setSimplePriceMin={setSimplePriceMin}
          simplePriceMax={simplePriceMax}
          setSimplePriceMax={setSimplePriceMax}
          summerAvailable={summerAvailable}
          setSummerAvailable={setSummerAvailable}
          winterAvailable={winterAvailable}
          setWinterAvailable={setWinterAvailable}
          rentalType={rentalType}
          setRentalType={setRentalType}
          rentalTypeOptions={RENTAL_TYPE_OPTIONS}
          showRefugeDormitoryPriceField={showRefugeDormitoryPriceField}
          dormitoryPrice={dormitoryPrice}
          setDormitoryPrice={setDormitoryPrice}
          dormitoryPriceMax={dormitoryPriceMax}
          setDormitoryPriceMax={setDormitoryPriceMax}
          showRefugeExclusivePriceField={showRefugeExclusivePriceField}
          exclusivePrice={exclusivePrice}
          setExclusivePrice={setExclusivePrice}
          exclusivePriceMax={exclusivePriceMax}
          setExclusivePriceMax={setExclusivePriceMax}
          fee={fee}
          setFee={setFee}
          feeOptions={FEE_OPTIONS}
          priceMin={priceMin}
          setPriceMin={setPriceMin}
          summerPrice={summerPrice}
          setSummerPrice={setSummerPrice}
          winterPrice={winterPrice}
          setWinterPrice={setWinterPrice}
          priceDate={priceDate}
          setPriceDate={setPriceDate}
          cost={cost}
          setCost={setCost}
          costOptions={COST_OPTIONS}
          showRefugeExclusiveOccupationField={showRefugeExclusiveOccupationField}
          exclusiveOccupation={exclusiveOccupation}
          setExclusiveOccupation={setExclusiveOccupation}
          exclusiveOccupationOptions={EXCLUSIVE_OCCUPATION_OPTIONS}
          showRefugePriceDateField={showRefugePriceDateField}
          reservation={reservation}
          setReservation={setReservation}
          reservationOptions={RESERVATION_OPTIONS}
          reservationUrl={reservationUrl}
          setReservationUrl={setReservationUrl}
          reservationEmail={reservationEmail}
          setReservationEmail={setReservationEmail}
          reservationPhone={reservationPhone}
          setReservationPhone={setReservationPhone}
        />
      )}

      {(isRefuge || isCamping) && (
        <section
          className="location-form__section location-form__pane"
          data-tab="activities"
          id={showTabs ? 'location-form-panel-activities' : undefined}
          role={showTabs ? 'tabpanel' : undefined}
          aria-labelledby={showTabs ? tr('LocationManagementForm.location_form_tab_activities_location') : 'location-form-activities'}
          hidden={showTabs && activeTab !== 'activities'}
        >
          <h3 id="location-form-activities" className="location-form__section-title">
            {tr('LocationManagementForm.activites')}
          </h3>
          <div className="location-form__field">
            {activities.length > 0 ? (
            <div className="location-form__checklist">
              {activities.map((a) => (
                <label key={a.id} className="location-form__checkbox-wrap">
                  <input
                    type="checkbox"
                    checked={activityIds.includes(a.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setActivityIds([...activityIds, a.id])
                      } else {
                        setActivityIds(activityIds.filter((id) => id !== a.id))
                      }
                    }}
                    disabled={isLoading}
                  />
                  <span>{a.name}</span>
                </label>
              ))}
            </div>
            ) : (
              <p className="location-form__hint">{tr('LocationManagementForm.aucune_activite_disponible_moment')}</p>
            )}
          </div>
        </section>
      )}


      {isCamping && (filterOptions?.placement_types?.length ?? 0) > 0 && (
        <section
          className="location-form__section location-form__pane"
          data-tab="equip"
          aria-labelledby="location-form-placement-types"
          hidden={showTabs && activeTab !== 'equip'}
        >
          <h3 id="location-form-placement-types" className="location-form__section-title">
            {tr('LocationManagementForm.types_emplacements')}
          </h3>
          <div className="location-form__field">
            <div className="location-form__checklist">
              {(filterOptions?.placement_types ?? []).map((pt) => (
                <label key={pt.id} className="location-form__checkbox-wrap">
                  <input
                    type="checkbox"
                    checked={placementTypeIds.includes(pt.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPlacementTypeIds([...placementTypeIds, pt.id])
                      } else {
                        setPlacementTypeIds(placementTypeIds.filter((id) => id !== pt.id))
                      }
                    }}
                    disabled={isLoading}
                  />
                  <span>{pt.name}</span>
                </label>
              ))}
            </div>
          </div>
        </section>
      )}

      <section
        className="location-form__section location-form__section--status location-form__pane"
        data-tab="info"
        aria-labelledby="location-form-status"
        hidden={showTabs && activeTab !== 'info'}
      >
        <h3 id="location-form-status" className="location-form__section-title">
          {tr('LocationManagementForm.statut')}
        </h3>
        <div className="location-form__field">
          <label htmlFor="location-form-status-select" className="location-form__label">
            {tr('LocationManagementForm.visibilite')}
          </label>
          <select
            id="location-form-status-select"
            className="location-form__select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={isLoading}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <p className="location-form__hint">
            {tr('LocationManagementForm.publie_visible_carte_brouillon_visible')}
          </p>
        </div>
      </section>

      </div>

      {(isRefuge || isCamping) && (
        <section
          className="location-form__section location-form__pane"
          data-tab="photos"
          id={showTabs ? 'location-form-panel-photos' : undefined}
          role={showTabs ? 'tabpanel' : undefined}
          aria-labelledby={showTabs ? 'location-form-tab-photos' : undefined}
          hidden={showTabs && activeTab !== 'photos'}
        >
          <h3 className="location-form__section-title">{tr('LocationManagementForm.photos')}</h3>
          {isEditMode && location?.id ? (
            <PrimaryGalleryEditor
              organizationId={organizationId}
              locationId={location.id}
              primaryGallery={location.primary_gallery}
              locationType={location.type}
              disabled={isLoading}
            />
          ) : (
            <p className="location-form__hint">
              {tr('LocationManagementForm.enregistrez_abord_lieu_pouvoir_ajouter')}
            </p>
          )}
        </section>
      )}

      {!showTabs && isEditMode && location?.id && (
        <PrimaryGalleryEditor
          organizationId={organizationId}
          locationId={location.id}
          primaryGallery={location.primary_gallery}
          locationType={location.type}
          disabled={isLoading}
        />
      )}

      <div className="location-form__actions">
        <button
          type="button"
          className="location-form__btn location-form__btn--cancel"
          onClick={onCancel}
          disabled={isLoading}
        >
          {tr('LocationManagementForm.annuler')}
        </button>
        <button
          type="submit"
          className="location-form__btn location-form__btn--submit"
          disabled={isLoading}
        >
          {isLoading ? tr('LocationManagementForm.enregistrement') : isEditMode ? tr('LocationManagementForm.enregistrer') : tr('LocationManagementForm.creer')}
        </button>
      </div>
    </form>
  )
}
